import React, { useMemo, useRef, useEffect } from "react";

interface DustBackgroundProps {
  className?: string;
  count?: number;
  color?: string;
}

interface Mote {
  id: number;
  x: number;
  y: number;
  size: number;
  opacity: number;
  duration: number;
  delay: number;
  driftX: number;
  driftY: number;
  blur: number;
}

/**
 * Western era hero effect: warm dust motes drifting through sun-baked air.
 * Slow horizontal drift with subtle vertical sway evokes desert wind and
 * golden-hour light filtering through a frontier saloon or open plain.
 */
const DustBackground: React.FC<DustBackgroundProps> = ({
  className = "",
  count = 60,
  color = "rgba(255, 200, 120, 0.55)",
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const motesRef = useRef<(HTMLSpanElement | null)[]>([]);
  const rafRef = useRef<number | null>(null);

  const motes = useMemo<Mote[]>(
    () =>
      Array.from({ length: count }, (_, i) => {
        const size = 1 + Math.random() * 4;
        return {
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size,
          opacity: 0.25 + Math.random() * 0.55,
          duration: 14 + Math.random() * 22,
          delay: -(Math.random() * 30),
          driftX: 40 + Math.random() * 120,
          driftY: -20 + Math.random() * 40,
          blur: size > 2.5 ? 1 : 0,
        };
      }),
    [count]
  );

  // Per-mote cursor reaction: motes near the cursor are nudged away,
  // distant motes barely move. Uses a single window listener and rAF batch.
  useEffect(() => {
    const RADIUS = 140; // px — only motes within this radius react
    const MAX_PUSH = 18; // px — strongest displacement at the cursor
    const onMove = (e: MouseEvent) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        for (let i = 0; i < motes.length; i++) {
          const node = motesRef.current[i];
          if (!node) continue;
          const m = motes[i];
          const mx = (m.x / 100) * rect.width;
          const my = (m.y / 100) * rect.height;
          const dx = mx - cx;
          const dy = my - cy;
          const dist = Math.hypot(dx, dy);
          if (dist > RADIUS || dist === 0) {
            node.style.transform = "translate3d(0,0,0)";
            continue;
          }
          const strength = (1 - dist / RADIUS) * MAX_PUSH;
          const ox = (dx / dist) * strength;
          const oy = (dy / dist) * strength;
          node.style.transform = `translate3d(${ox.toFixed(2)}px, ${oy.toFixed(2)}px, 0)`;
        }
      });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [motes]);

  return (
    <div ref={containerRef} className={`overflow-hidden relative ${className}`}>
      {/* Warm sunlight wash */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 70% 30%, rgba(255, 180, 90, 0.18) 0%, rgba(200, 120, 40, 0.06) 40%, transparent 75%)",
        }}
      />

      {/* Sunbeam shafts */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(105deg, transparent 35%, rgba(255, 210, 140, 0.08) 48%, transparent 62%)",
          mixBlendMode: "screen",
        }}
      />

      <div className="absolute inset-0 pointer-events-none">
        {motes.map((mote, i) => (
          <span
            key={mote.id}
            ref={(el) => {
              motesRef.current[i] = el;
            }}
            className="absolute"
            style={{
              left: `${mote.x}%`,
              top: `${mote.y}%`,
              transition: "transform 500ms cubic-bezier(0.22, 1, 0.36, 1)",
              willChange: "transform",
            }}
          >
            <span
              className="block rounded-full"
              style={{
                width: mote.size,
                height: mote.size,
                background: color,
                opacity: mote.opacity,
                filter: mote.blur ? `blur(${mote.blur}px)` : undefined,
                boxShadow: `0 0 ${mote.size * 3}px ${color}`,
                animation: `dust-drift ${mote.duration}s linear ${mote.delay}s infinite`,
                ["--drift-x" as string]: `${mote.driftX}px`,
                ["--drift-y" as string]: `${mote.driftY}px`,
              }}
            />
          </span>
        ))}
      </div>

      <style>{`
        @keyframes dust-drift {
          0% {
            transform: translate(0, 0);
            opacity: 0;
          }
          10% {
            opacity: var(--mote-opacity, 0.6);
          }
          50% {
            transform: translate(calc(var(--drift-x) * 0.55), calc(var(--drift-y) * 0.55 - 6px));
          }
          90% {
            opacity: var(--mote-opacity, 0.6);
          }
          100% {
            transform: translate(var(--drift-x), var(--drift-y));
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default DustBackground;
